import type { EditionDto } from '../../../api/generated/types.gen';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ALL_EDITIONS_SENTINEL } from '../../constants/editions';

type EditionDTO = EditionDto;

interface EditionSelectorProps {
    editions: EditionDTO[];
    currentEditionId?: string;
    onChange: (editionId: string) => void;
    disabled?: boolean;
    /** ADMIN only. Adauga itemul "Toate edițiile" ca prima optiune. @default false */
    allowAllEditions?: boolean;
    /** True cand itemul "Toate edițiile" e selectat. Necesita allowAllEditions=true. @default false */
    isAllEditionsSelected?: boolean;
}

/**
 * EditionSelector Component
 *
 * Reusable dropdown for selecting program editions.
 * Displays edition name with "(curent)" badge for current edition.
 *
 * Foloseste acelasi shadcn `Select` ca restul filtrelor de raport. Pana in
 * 2026-08-19 era un `<select>` nativ cu `border-magenta` SI `ring-1 ring-magenta`
 * — contur verde dublu, alta sageata si alta eticheta decat controalele de langa el.
 * → docs/design-system/REPORT_POLISH_PLAN.md, constatarile #9 si #10
 *
 * @example
 * <EditionSelector
 *   editions={editions}
 *   currentEditionId={currentEdition?.id}
 *   onChange={changeEdition}
 *   disabled={isLoading}
 * />
 */
export function EditionSelector({
    editions,
    currentEditionId,
    onChange,
    disabled = false,
    allowAllEditions = false,
    isAllEditionsSelected = false
}: EditionSelectorProps) {
    if (editions.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col">
            <label htmlFor="edition-selector" className="ecotic-field-label">
                Ediție
            </label>
            <Select
                value={isAllEditionsSelected ? ALL_EDITIONS_SENTINEL : (currentEditionId || '')}
                onValueChange={onChange}
                disabled={disabled}
            >
                <SelectTrigger
                    id="edition-selector"
                    aria-label="Ediție"
                    className="ecotic-field-control w-full min-w-[232px]"
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {allowAllEditions && (
                        <SelectItem value={ALL_EDITIONS_SENTINEL}>Toate edițiile</SelectItem>
                    )}
                    {editions.map((edition) => (
                        <SelectItem key={edition.id} value={edition.id!}>
                            {edition.nume}
                            {edition.isCurrentEdition && ' (curentă)'}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
